import { useEffect, useRef, useState, useCallback } from 'react';
import { WebSocketService } from '../../services/realtime/WebSocketService';
import { logger } from '../../utils/logger';
import { TranscriptMessage } from './types';

interface UseContextManagerProps {
  messages: TranscriptMessage[];
  websocketServiceRef: React.MutableRefObject<WebSocketService | null>;
  systemInstructionsRef: React.MutableRefObject<string>;
  updateSession: (config: any) => Promise<void>;
  maxMessages?: number;
  keepMessages?: number;
}

export const useContextManager = ({
  messages,
  websocketServiceRef,
  systemInstructionsRef,
  updateSession,
  maxMessages = 20,
  keepMessages = 10,
}: UseContextManagerProps) => {
  const [isSummarizing, setIsSummarizing] = useState(false);
  const currentSummaryRef = useRef<string | null>(null);

  const summarizeContext = useCallback(async () => {
    if (isSummarizing || messages.length <= maxMessages) return;

    const websocketService = websocketServiceRef.current;
    if (!websocketService || !websocketService.isConnected()) return;

    setIsSummarizing(true);
    logger.info(`[ContextManager] Triggering summarization. Total messages: ${messages.length}`);

    try {
      // 1. Identify messages to summarize (oldest ones, keeping the most recent 'keepMessages')
      const messagesToSummarize = messages.slice(0, messages.length - keepMessages);
      
      // We only summarize messages that have an ID (meaning they were sent to/from the server)
      const validMessagesToSummarize = messagesToSummarize.filter(m => m.id);
      
      if (validMessagesToSummarize.length === 0) {
        setIsSummarizing(false);
        return;
      }

      const itemIdsToDelete = validMessagesToSummarize.map(m => m.id as string);

      // Format for the backend
      const formattedMessages = validMessagesToSummarize.map(m => ({
        role: m.speaker === 'ai' ? 'assistant' : 'user',
        content: m.text
      }));

      // 2. Call backend to generate summary
      const response = await fetch('/api/realtime-ws/summarize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: formattedMessages,
          previous_summary: currentSummaryRef.current
        }),
      });

      if (!response.ok) {
        throw new Error(`Summarization failed with status: ${response.status}`);
      }

      const data = await response.json();
      const newSummary = data.summary;
      currentSummaryRef.current = newSummary;

      logger.info(`[ContextManager] Summarization complete. Deleting ${itemIdsToDelete.length} items from context.`);

      // 3. Delete old items from OpenAI context
      itemIdsToDelete.forEach(itemId => {
        websocketService.deleteConversationItem(itemId);
      });

      // 4. Update session instructions with the new summary
      const baseInstructions = systemInstructionsRef.current;
      
      // Remove any previous summary block if it exists (though we usually just append to the base)
      const cleanBaseInstructions = baseInstructions.split('\n\n[CONVERSATION HISTORY SUMMARY]')[0];
      
      const newInstructions = `${cleanBaseInstructions}\n\n[CONVERSATION HISTORY SUMMARY]\n${newSummary}`;
      
      // We only update the instructions, preserving other session config
      await updateSession({ instructions: newInstructions });
      
      logger.info('[ContextManager] Session instructions updated with new summary.');

    } catch (error) {
      logger.error('[ContextManager] Error during context summarization:', error);
    } finally {
      setIsSummarizing(false);
    }
  }, [messages, isSummarizing, maxMessages, keepMessages, websocketServiceRef, systemInstructionsRef, updateSession]);

  // Monitor messages array and trigger summarization when limit is reached
  useEffect(() => {
    if (messages.length > maxMessages && !isSummarizing) {
      summarizeContext();
    }
  }, [messages.length, maxMessages, isSummarizing, summarizeContext]);

  return {
    isSummarizing,
    currentSummary: currentSummaryRef.current,
    forceSummarize: summarizeContext
  };
};
